import React from 'react';
import {
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  Link,
  useMediaQuery,
  useTheme,
  Stack,
  Box,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { OfferStatus } from '@prisma/client';
import { FileTypeChip } from './FileTypeChip';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import offersService from '../../../../api/services/offers';
import { baseUrl } from '../../../../api/api';
// eslint-disable-next-line max-len
import { RootState } from '../../../../store/store';
// eslint-disable-next-line max-len
import { selectUploadsByListingId } from '../../../../store/selectors/uploads';

export interface SignableDocument {
  id: string;
  name: string;
  status: string;
  // The API resolves this from the requesting user's role on the offer, and
  // returns null when they are not one of the document's signers.
  signSlug: string | null;
  pdfUrl: string | null;
  createdAt: string;
}

interface Props {
  listingId: string;
  buyerId?: string; // If provided, only show files for this buyer id
  offerId?: string;
  token?: string;
  signableDocuments?: SignableDocument[];
  offerStatus?: OfferStatus;
  // Buyers sign from the property row workflow, so the files list is read-only
  // for them. Defaults to true for seller/admin views.
  allowSigning?: boolean;
}

// Offer documents are generated while the agent is still reviewing the offer,
// so a signing slug exists long before anyone is meant to sign. Signing only
// opens up once the offer has been released to the parties.
const RELEASED_OFFER_STATUSES: OfferStatus[] = [
  OfferStatus.REVIEWED,
  OfferStatus.COUNTERED,
  OfferStatus.SIGNED,
  OfferStatus.ACCEPTED,
];

const getSignableTypeColor = (name: string): 'secondary' | 'info' | 'default' => {
  const lower = name.toLowerCase();
  if (lower.includes('purchase agreement')) return 'secondary';
  if (lower.includes('addendum') || lower.includes('exhibit')) return 'info';
  return 'default';
};

const SignableTypeChip: React.FC<{ name: string }> = ({ name }) => {
  const color = getSignableTypeColor(name);
  return (
    <Chip
      label={name}
      color={color}
      size="small"
      variant="filled"
      sx={{
        fontWeight: 600,
        '&.MuiChip-filled': {
          color: (theme) => (color === 'default' ? theme.palette.grey[900] : '#fff'),
        },
      }}
    />
  );
};

const SignedIndicatorChip: React.FC = () => (
  <Chip
    label="Signed"
    color="success"
    size="small"
    variant="outlined"
    sx={{ fontWeight: 600 }}
  />
);

const PendingSignatureChip: React.FC<{ released: boolean }> = ({
  released,
}) => (
  <Chip
    label={released ? 'Awaiting signature' : 'Pending review'}
    size="small"
    variant="outlined"
    sx={{ fontWeight: 600 }}
  />
);

const sortSignables = (docs: SignableDocument[]) =>
  [...docs].sort((a, b) => {
    if (a.status === b.status) {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }
    return a.status === 'all_signed' ? 1 : -1;
  });

const formatSignableDate = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: undefined,
  });

const FilesTable: React.FC<Props> = ({
  listingId,
  buyerId,
  offerId,
  token,
  signableDocuments = [],
  offerStatus,
  allowSigning = true,
}) => {
  const sortedSignables = sortSignables(signableDocuments);
  const offerReleased =
    !!offerStatus && RELEASED_OFFER_STATUSES.includes(offerStatus);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isDownloading, setIsDownloading] = useState(false);
  const docs = useSelector((state: RootState) =>
    selectUploadsByListingId(state, listingId)
  );

  // When accessing with a token (public portal), show all documents
  // Otherwise, filter by buyerId if provided
  const filteredDocs = token
    ? docs
    : buyerId
    ? docs.filter((doc) => doc.buyerId === buyerId)
    : docs;

  // Sort documents with PURCHASE_AGREEMENT first
  const sortedDocs = [...filteredDocs].sort((a, b) => {
    if (a.docType === 'PURCHASE_AGREEMENT' && b.docType !== 'PURCHASE_AGREEMENT') {
      return -1;
    }
    if (a.docType !== 'PURCHASE_AGREEMENT' && b.docType === 'PURCHASE_AGREEMENT') {
      return 1;
    }
    return 0;
  });

  const handleDownloadAll = async () => {
    if (!offerId) return;

    // For public portal access (token-based), open the URL directly so the
    // browser displays the PDF inline — works reliably on mobile and desktop
    if (token) {
      window.open(
        `${baseUrl}/offers/${offerId}/download-documents?token=${token}`,
        '_blank'
      );
      return;
    }

    // For authenticated users, fetch as blob since the JWT is in headers
    setIsDownloading(true);
    try {
      const blob = await offersService.downloadDocuments(offerId);
      const pdfUrl = window.URL.createObjectURL(
        new Blob([blob], { type: 'application/pdf' })
      );
      window.open(pdfUrl, '_blank');

      setTimeout(() => {
        window.URL.revokeObjectURL(pdfUrl);
      }, 10000);
    } catch (error) {
      console.error('Error downloading documents:', error);
      alert('Failed to load documents. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (isMobile) {
    return (
      <Stack spacing={2}>
        {offerId && sortedDocs.length > 0 && (
          <Box px={2} pt={2}>
            <Button
              variant="contained"
              fullWidth
              startIcon={
                isDownloading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <DownloadIcon />
                )
              }
              onClick={handleDownloadAll}
              disabled={isDownloading}
            >
              {isDownloading ? 'Loading...' : 'View All as PDF'}
            </Button>
          </Box>
        )}
        {sortedDocs.length === 0 && sortedSignables.length === 0 && (
          <Typography p={2} pt={0}>
            No documents
          </Typography>
        )}
        {sortedSignables.map((doc) => {
          const signSlug = doc.signSlug;
          const isSigned = doc.status === 'all_signed';
          const canSign =
            !isSigned && offerReleased && allowSigning && !!signSlug;
          return (
            <React.Fragment key={`signable-${doc.id}`}>
              <Box p={2}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      Name
                    </Typography>
                    <Typography variant="body2">{doc.name}</Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      Type
                    </Typography>
                    <SignableTypeChip name={doc.name} />
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      Submitted
                    </Typography>
                    <Typography variant="body2">
                      {formatSignableDate(doc.createdAt)}
                    </Typography>
                  </Stack>

                  {isSigned && (
                    <Box sx={{ alignSelf: 'flex-start' }}>
                      <SignedIndicatorChip />
                    </Box>
                  )}
                  {canSign && (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      href={`https://docuseal.co/s/${signSlug}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Sign
                    </Button>
                  )}
                  {!isSigned && !canSign && (
                    <Box sx={{ alignSelf: 'flex-start' }}>
                      <PendingSignatureChip released={offerReleased} />
                    </Box>
                  )}
                </Stack>
              </Box>
              <Divider />
            </React.Fragment>
          );
        })}
        {sortedDocs.map((doc) => (
          <React.Fragment key={doc.id}>
            <Box p={2}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Name
                  </Typography>
                  <Typography variant="body2">{doc.fileName}</Typography>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Type
                  </Typography>
                  {doc.docType ? (
                    <FileTypeChip docType={doc.docType} />
                  ) : (
                    <Typography variant="body2">Unknown</Typography>
                  )}
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Submitted By
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {doc.user?.firstName} {doc.user?.lastName}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Submitted On
                  </Typography>
                  <Typography variant="body2">
                    {new Date(doc.createdAt).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                      second: undefined,
                    })}
                  </Typography>
                </Stack>

                <Link
                  target="_blank"
                  href={doc.url}
                  rel="noreferrer"
                  sx={{ alignSelf: 'flex-start', width: '100%' }}
                >
                  <Button variant="outlined" fullWidth>
                    View
                  </Button>
                </Link>
              </Stack>
            </Box>
            <Divider />
          </React.Fragment>
        ))}
      </Stack>
    );
  }

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  Name
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  Type
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  Submitted
                </Typography>
              </TableCell>
              <TableCell>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  spacing={1}
                >
                  <Typography variant="body2" fontWeight={600}>
                    Actions
                  </Typography>
                  {offerId && sortedDocs.length > 0 && (
                    <Tooltip title="View All as PDF">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={handleDownloadAll}
                        disabled={isDownloading}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                          '&:disabled': {
                            bgcolor: 'action.disabledBackground',
                          },
                        }}
                      >
                        {isDownloading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <DownloadIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedDocs.length === 0 && sortedSignables.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography>No documents</Typography>
                </TableCell>
              </TableRow>
            )}
            {sortedSignables.map((doc) => {
              const signSlug = doc.signSlug;
              const isSigned = doc.status === 'all_signed';
              const canSign =
                !isSigned && offerReleased && allowSigning && !!signSlug;
              return (
                <TableRow key={`signable-${doc.id}`} hover>
                  <TableCell>{doc.name}</TableCell>
                  <TableCell>
                    <SignableTypeChip name={doc.name} />
                  </TableCell>
                  <TableCell>{formatSignableDate(doc.createdAt)}</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    {isSigned && <SignedIndicatorChip />}
                    {canSign && (
                      <Button
                        variant="contained"
                        color="primary"
                        href={`https://docuseal.co/s/${signSlug}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Sign
                      </Button>
                    )}
                    {!isSigned && !canSign && (
                      <PendingSignatureChip released={offerReleased} />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {sortedDocs.map((doc) => {
              return (
                <TableRow
                  key={doc.id}
                  sx={{ '& > *': { cursor: 'pointer' } }}
                  hover={true}
                  //   onClick={() => navigate(`/buyers/${buyer.id}`)}
                >
                  <TableCell>{doc.fileName}</TableCell>
                  <TableCell>
                    {doc.docType ? (
                      <FileTypeChip docType={doc.docType} />
                    ) : (
                      <Typography variant="body2">Unknown</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={1}>
                      <Typography variant="body2" fontWeight={600}>
                        {doc.user?.firstName} {doc.user?.lastName}
                      </Typography>
                      <Typography variant="body2">
                        {new Date(doc.createdAt).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: 'numeric',
                          second: undefined,
                        })}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    <Link target="_blank" href={doc.url} rel="noreferrer">
                      <Button variant="outlined">View</Button>
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default FilesTable;
